/* eslint-disable complexity */
/* eslint-disable max-lines */
import React, { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Column, format, getUser, Row } from "@gamedev-web/gamedev-sdk-core";

import { isGameConceptSoftRejected } from "../../helpers/gameConceptHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import {
  CrmApplicationStatus,
  CrmGameConceptStatus,
  CrmGameConceptStatusOrder2,
} from "../../models/crmStatus";
import { GameInformationConceptDetailsDataType } from "../../models/forms";
import { Programs } from "../../models/programsConfig";
import {
  AaaApplicationStep,
  IdXboxApplicationStep,
  PitchApplicationStep,
} from "../../models/status";
import {
  GameInformationFormId,
  GameInformationSubFormsId,
} from "../../pages/GameInformationPage/GameInformationPageConstants";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import {
  getAaaApplicationStep,
  getCrmApplicationStatus,
  getCurrentGameConcept,
  getGameConceptsList,
  getIdXboxApplicationStep,
  getIsGdkReceived,
  getPitchApplicationStep,
  getSubFormData,
} from "../../redux/selectors";

import { IdXboxGameConceptDataSet } from "../../server/crm/api/models/forms";
import { validateGameConcept } from "../../server/utils/dataValidation/dataFilter";
import { AchievementIcon, SolidAchievementIcon, TimerIcon } from "../icons";
import { RightPaneBox } from "../RightPaneBox";
import * as styles from "./ApplicationStatusBox.module.css";

type ApplicationStatusData = Readonly<{
  title: string;
  subtitle: string;
  icon?: ReactNode;
}>;

interface ApplicationStatusBoxProps {
  program: Programs;
}

export const ApplicationStatusBox = ({ program }: ApplicationStatusBoxProps) => {
  const [
    applicationStatusBeingReviewedSubtitle,
    applicationStatusDefaultEmail,
    pitchStatusBoxIntroApplicationUnderReviewTitle,
    pitchStatusBoxIntroApplicationUnderReviewSubtitle,
    pitchStatusBoxIntroNDAWaitingTitle,
    pitchStatusBoxIntroNDAWaitingSubtitle,
    aaaApplicationStatusBeingReviewedTitle,
    aaaApplicationStatusBoxReadyToApplyTitle,
    aaaApplicationStatusBoxReadyToApplySubtitle,
    aaaApplicationStatusMoreInfoNeededTitle,
    aaaApplicationStatusMoreInfoNeededSubtitle,
  ] = useStrings(
    "applicationStatusBeingReviewedSubtitle",
    "applicationStatusDefaultEmail",
    "pitchStatusBoxIntroApplicationUnderReviewTitle",
    "pitchStatusBoxIntroApplicationUnderReviewSubtitle",
    "pitchStatusBoxIntroNDAWaitingTitle",
    "pitchStatusBoxIntroNDAWaitingSubtitle",
    "aaaApplicationStatusBeingReviewedTitle",
    "aaaApplicationStatusBoxReadyToApplyTitle",
    "aaaApplicationStatusBoxReadyToApplySubtitle",
    "aaaApplicationStatusMoreInfoNeededTitle",
    "aaaApplicationStatusMoreInfoNeededSubtitle"
  );
  let currentStep = "";
  const currentAaaStep = useSelector(getAaaApplicationStep);
  const currentPitchStep = useSelector(getPitchApplicationStep);

  switch (program) {
    case Programs.Aaa:
      currentStep = currentAaaStep;
      break;
    case Programs.Pitch:
      currentStep = currentPitchStep;
      break;
  }

  const user = useSelector(getUser);
  const currentGameConcept = useSelector(getCurrentGameConcept);
  const [content, setContent] = useState({ title: "", subtitle: "" } as ApplicationStatusData);

  useEffect(() => {
    switch (currentStep) {
      case AaaApplicationStep.ApplicationIsBeingReviewed:
        setContent({
          title: aaaApplicationStatusBeingReviewedTitle,
          subtitle: format(
            applicationStatusBeingReviewedSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <TimerIcon />,
        });
        break;
      case AaaApplicationStep.ApplicationReadyForReview:
        if (currentGameConcept?.crmGameConceptStatus === CrmGameConceptStatus.MoreInfoNeeded) {
          setContent({
            title: aaaApplicationStatusMoreInfoNeededTitle,
            subtitle: aaaApplicationStatusMoreInfoNeededSubtitle,
            icon: <TimerIcon />,
          });
        } else {
          setContent({
            title: aaaApplicationStatusBoxReadyToApplyTitle,
            subtitle: aaaApplicationStatusBoxReadyToApplySubtitle,
            icon: <AchievementIcon />,
          });
        }
        break;
      case PitchApplicationStep.NdaUnderReview:
        setContent({
          title: pitchStatusBoxIntroApplicationUnderReviewTitle,
          subtitle: format(
            pitchStatusBoxIntroApplicationUnderReviewSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <TimerIcon />,
        });
        break;
      case PitchApplicationStep.NdaSent:
        setContent({
          title: pitchStatusBoxIntroNDAWaitingTitle,
          subtitle: pitchStatusBoxIntroNDAWaitingSubtitle,
          icon: <TimerIcon />,
        });
        break;
      default:
        setContent({ title: "", subtitle: "" });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, user]);

  if (!currentStep || !content.title) {
    return null;
  }

  return (
    <RightPaneBox>
      <Row>
        <Column>
          {content.icon && <Row className={styles.row}>{content.icon}</Row>}
          <Row className={styles.row}>
            <p className={styles.title}>{content.title}</p>
          </Row>
          <Row className={styles.row}>
            <p className={styles.subtitle}>{content.subtitle}</p>
          </Row>
        </Column>
      </Row>
    </RightPaneBox>
  );
};

export const IdXboxWelcomeApplicationStatusBox = () => {
  const [
    applicationStatusBoxIntroductionReadyToApplyTitle,
    applicationStatusBoxIntroductionReadyToApplySubtitle,
    applicationStatusSignNdaTitle,
    applicationStatusSignNdaSubtitle,
    applicationStatusBoxReadyToApplyTitle,
    applicationStatusWaitingForGameConceptTitle,
    applicationStatusBoxReadyToApplySubtitle,
    applicationStatusBeingReviewedTitle,
    applicationStatusNdaBeingReviewedSubtitle,
    applicationStatusBeingReviewedSubtitle,
    applicationStatusAdditionalAgreementsTitleIdXboxWelcomePage,
    applicationStatusAdditionalAgreementsSubtitle,
    applicationStatusDefaultEmail,
    applicationStatusGdkMissingSubtitle,
  ] = useStrings(
    "applicationStatusBoxIntroductionReadyToApplyTitle",
    "applicationStatusBoxIntroductionReadyToApplySubtitle",
    "applicationStatusSignNdaTitle",
    "applicationStatusSignNdaSubtitle",
    "applicationStatusBoxReadyToApplyTitle",
    "applicationStatusWaitingForGameConceptTitle",
    "applicationStatusBoxReadyToApplySubtitle",
    "applicationStatusBeingReviewedTitle",
    "applicationStatusNdaBeingReviewedSubtitle",
    "applicationStatusBeingReviewedSubtitle",
    "applicationStatusAdditionalAgreementsTitleIdXboxWelcomePage",
    "applicationStatusAdditionalAgreementsSubtitle",
    "applicationStatusDefaultEmail",
    "applicationStatusGdkMissingSubtitle"
  );

  const isGdkReceived = useSelector(getIsGdkReceived);
  const currentStep = useSelector(getIdXboxApplicationStep);
  const user = useSelector(getUser);
  const crmIdXboxApplicationStatus = useSelector(getCrmApplicationStatus);
  const gameConceptsList = useSelector(getGameConceptsList) as IdXboxGameConceptDataSet;
  const [content, setContent] = useState({ title: "", subtitle: "" } as ApplicationStatusData);

  useEffect(() => {
    const handleGameConcepts = async () => {
      let highestCrmGameConceptStatus;
      let highestStatusNumericValue = -1;

      for (const key in gameConceptsList) {
        const gameConcept = gameConceptsList[key];
        const gameConceptStatus = gameConcept.state.status;

        const statusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];

        if (statusNumericValue > highestStatusNumericValue) {
          highestStatusNumericValue = CrmGameConceptStatusOrder2[gameConceptStatus];
          highestCrmGameConceptStatus = gameConceptStatus;
        }
      }

      if (highestCrmGameConceptStatus === CrmGameConceptStatus.Accepted && !isGdkReceived) {
        setContent({
          title: applicationStatusAdditionalAgreementsTitleIdXboxWelcomePage,
          subtitle: format(
            applicationStatusGdkMissingSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <SolidAchievementIcon />,
        });
      } else if (highestCrmGameConceptStatus === CrmGameConceptStatus.AdditionalContractsSent) {
        setContent({
          title: applicationStatusAdditionalAgreementsTitleIdXboxWelcomePage,
          subtitle: format(
            applicationStatusAdditionalAgreementsSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <SolidAchievementIcon />,
        });
      } else if (highestCrmGameConceptStatus === CrmGameConceptStatus.UnderReview) {
        setContent({
          title: applicationStatusBeingReviewedTitle,
          subtitle: format(
            applicationStatusBeingReviewedSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <TimerIcon />,
        });
      } else {
        let atLeastOneGameConceptReadyToApply = false;
        for (const key in gameConceptsList) {
          const gameConcept = gameConceptsList[key];
          const gameConceptValidation = await validateGameConcept(gameConcept);
          if (gameConceptValidation.result) {
            atLeastOneGameConceptReadyToApply = true;
            break;
          }
        }
        setContent({
          title: atLeastOneGameConceptReadyToApply
            ? applicationStatusBoxReadyToApplyTitle
            : applicationStatusWaitingForGameConceptTitle,
          subtitle: applicationStatusBoxReadyToApplySubtitle,
          icon: <AchievementIcon />,
        });
      }
    };

    switch (currentStep) {
      case IdXboxApplicationStep.IntroductionReadyToSubmit:
        setContent({
          title: applicationStatusBoxIntroductionReadyToApplyTitle,
          subtitle: applicationStatusBoxIntroductionReadyToApplySubtitle,
          icon: <AchievementIcon />,
        });
        break;
      case IdXboxApplicationStep.NdaUnderReview:
        setContent({
          title: applicationStatusBeingReviewedTitle,
          subtitle: format(
            applicationStatusNdaBeingReviewedSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <TimerIcon />,
        });
        break;
      case IdXboxApplicationStep.ApplicationIsBeingReviewed:
        setContent({
          title: applicationStatusBeingReviewedTitle,
          subtitle: format(
            applicationStatusBeingReviewedSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <TimerIcon />,
        });
        break;
      case IdXboxApplicationStep.NdaSent:
        setContent({
          title: applicationStatusSignNdaTitle,
          subtitle: format(
            applicationStatusSignNdaSubtitle,
            user?.emailAddress ? user.emailAddress : ""
          ),
          icon: <TimerIcon />,
        });
        break;
      default:
        setContent({ title: "", subtitle: "" });
        break;
    }

    if (crmIdXboxApplicationStatus === CrmApplicationStatus.NdaSigned) {
      handleGameConcepts();
    }
  }, [
    currentStep,
    user,
    crmIdXboxApplicationStatus,
    gameConceptsList,
    applicationStatusBoxIntroductionReadyToApplyTitle,
    applicationStatusBoxIntroductionReadyToApplySubtitle,
    applicationStatusBeingReviewedTitle,
    applicationStatusBeingReviewedSubtitle,
    applicationStatusSignNdaTitle,
    applicationStatusSignNdaSubtitle,
    applicationStatusNdaBeingReviewedSubtitle,
    applicationStatusWaitingForGameConceptTitle,
    applicationStatusDefaultEmail,
    applicationStatusAdditionalAgreementsTitleIdXboxWelcomePage,
    applicationStatusAdditionalAgreementsSubtitle,
    applicationStatusBoxReadyToApplyTitle,
    applicationStatusBoxReadyToApplySubtitle,
    applicationStatusGdkMissingSubtitle,
    isGdkReceived,
  ]);
  if (!content.title) {
    return <></>;
  }

  return (
    <RightPaneBox>
      <Row>
        <Column>
          {content.icon && <Row className={styles.row}>{content.icon}</Row>}
          <Row className={styles.row}>
            <p className={styles.title}>{content.title}</p>
          </Row>
          <Row className={styles.row}>
            <p className={styles.subtitle}>{content.subtitle}</p>
          </Row>
        </Column>
      </Row>
    </RightPaneBox>
  );
  // final curly bracket
};

export const IdXboxGameConceptApplicationStatusBox = () => {
  const [
    applicationStatusBoxReadyToApplyTitle,
    applicationStatusBoxReadyToApplySubtitle,
    applicationStatusBeingReviewedTitle,
    applicationStatusBeingReviewedSubtitle,
    applicationStatusAdditionalAgreementsTitle,
    applicationStatusAdditionalAgreementsSubtitle,
    applicationStatusDefaultGameTitle,
    applicationStatusDefaultEmail,
    applicationStatusMoreInfoNeededTitle,
    applicationStatusMoreInfoNeededSubtitle,
  ] = useStrings(
    "applicationStatusBoxReadyToApplyTitle",
    "applicationStatusBoxReadyToApplySubtitle",
    "applicationStatusBeingReviewedTitle",
    "applicationStatusBeingReviewedSubtitle",
    "applicationStatusAdditionalAgreementsTitle",
    "applicationStatusAdditionalAgreementsSubtitle",
    "applicationStatusDefaultGameTitle",
    "applicationStatusDefaultEmail",
    "applicationStatusMoreInfoNeededTitle",
    "applicationStatusMoreInfoNeededSubtitle"
  );

  const user = useSelector(getUser);
  const currentGameConcept = useSelector(getCurrentGameConcept);

  const [content, setContent] = useState({ title: "", subtitle: "" } as ApplicationStatusData);
  const [isReadyForReview, setIsReadyForReview] = useState(false);
  const gameInfoConceptSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.ConceptDetails)
  ) as GameInformationConceptDetailsDataType;

  const status = currentGameConcept?.crmGameConceptStatus;
  const subStatus = currentGameConcept?.crmGameConceptSubStatus;

  useEffect(() => {
    const calculateIsReadyForReview = async () => {
      if (currentGameConcept?.forms) {
        const result = await validateGameConcept(currentGameConcept.forms);
        setIsReadyForReview(result.result);
      } else {
        setIsReadyForReview(false);
      }
    };
    calculateIsReadyForReview();
  }, [currentGameConcept]);

  useEffect(() => {
    switch (status) {
      case CrmGameConceptStatus.UnderReview:
        setContent({
          title: applicationStatusBeingReviewedTitle,
          subtitle: format(
            applicationStatusBeingReviewedSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <TimerIcon />,
        });
        break;
      case CrmGameConceptStatus.MoreInfoNeeded:
        setContent({
          title: applicationStatusMoreInfoNeededTitle,
          subtitle: applicationStatusMoreInfoNeededSubtitle,
          icon: <TimerIcon />,
        });
        break;
      case CrmGameConceptStatus.Rejected:
        if (isGameConceptSoftRejected(status, subStatus)) {
          setContent({
            title: applicationStatusMoreInfoNeededTitle,
            subtitle: applicationStatusMoreInfoNeededSubtitle,
            icon: <TimerIcon />,
          });
        }
        break;
      case CrmGameConceptStatus.Unset:
        if (isReadyForReview) {
          setContent({
            title: applicationStatusBoxReadyToApplyTitle,
            subtitle: applicationStatusBoxReadyToApplySubtitle,
            icon: <AchievementIcon />,
          });
        }
        break;
      case CrmGameConceptStatus.AdditionalContractsSent:
        setContent({
          title: format(
            applicationStatusAdditionalAgreementsTitle,
            gameInfoConceptSubFormData?.gameTitle
              ? gameInfoConceptSubFormData.gameTitle
              : applicationStatusDefaultGameTitle
          ),
          subtitle: format(
            applicationStatusAdditionalAgreementsSubtitle,
            user?.emailAddress ? user.emailAddress : applicationStatusDefaultEmail
          ),
          icon: <SolidAchievementIcon />,
        });
        break;
      default:
        setContent({ title: "", subtitle: "" });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user, isReadyForReview]);

  if (!content.title) {
    return null;
  }

  return (
    <RightPaneBox>
      <Row>
        <Column>
          {content.icon && <Row className={styles.row}>{content.icon}</Row>}
          <Row className={styles.row}>
            <p className={styles.title}>{content.title}</p>
          </Row>
          <Row className={styles.row}>
            <p className={styles.subtitle}>{content.subtitle}</p>
          </Row>
        </Column>
      </Row>
    </RightPaneBox>
  );
};
